import axios from 'axios';
import API_CONFIG from '../config/api';

class ApiService {
  constructor() {
    this.baseURL = API_CONFIG.getApiUrl();
    this.apiKey = localStorage.getItem('dark_matter_api_key');
    this.jwtToken = localStorage.getItem('dark_matter_jwt_token');
    
    // Create axios instance
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: API_CONFIG.getAuthHeaders(this.apiKey, this.jwtToken)
    });
    
    // Add request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Update headers with current auth tokens
        const apiKey = localStorage.getItem('dark_matter_api_key');
        const jwtToken = localStorage.getItem('dark_matter_jwt_token');
        
        if (apiKey) {
          config.headers['X-API-Key'] = apiKey;
        }
        
        if (jwtToken) {
          config.headers['Authorization'] = `Bearer ${jwtToken}`;
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
    
    // Add response interceptor
    this.client.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized access
          this.clearAuth();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }
  
  // Authentication methods
  async register(userData) {
    const response = await this.client.post(API_CONFIG.ENDPOINTS.AUTH.REGISTER, userData);
    this.setAuth(response.data.api_key, response.data.jwt_token);
    return response.data;
  }
  
  async login(userData) {
    const response = await this.client.post(API_CONFIG.ENDPOINTS.AUTH.LOGIN, userData);
    this.setAuth(null, response.data.jwt_token);
    return response.data;
  }
  
  async validateCredentials() {
    const response = await this.client.post(API_CONFIG.ENDPOINTS.AUTH.VALIDATE);
    return response.data;
  }
  
  async getApiKeys() {
    const response = await this.client.get(API_CONFIG.ENDPOINTS.AUTH.API_KEYS);
    return response.data;
  }
  
  async createApiKey(permissions = ['read', 'write']) {
    const response = await this.client.post(API_CONFIG.ENDPOINTS.AUTH.API_KEYS, { permissions });
    return response.data;
  }
  
  async getRateLimits() {
    const response = await this.client.get(API_CONFIG.ENDPOINTS.AUTH.RATE_LIMITS);
    return response.data;
  }
  
  // AI Background Monitoring methods
  async getBackgroundSources() {
    const response = await this.client.get(API_CONFIG.ENDPOINTS.AI_BACKGROUND.SOURCES);
    return response.data;
  }
  
  async testBackgroundCheck(personData) {
    const response = await this.client.post(API_CONFIG.ENDPOINTS.AI_BACKGROUND.TEST_CHECK, personData);
    return response.data;
  }
  
  async startMonitoring(userId, personData) {
    const response = await this.client.post(API_CONFIG.ENDPOINTS.AI_BACKGROUND.START_MONITORING, {
      user_id: userId,
      person_data: personData
    });
    return response.data;
  }
  
  async stopMonitoring(userId) {
    const response = await this.client.post(API_CONFIG.ENDPOINTS.AI_BACKGROUND.STOP_MONITORING, {
      user_id: userId
    });
    return response.data;
  }
  
  async getMonitoringStatus() {
    const response = await this.client.get(API_CONFIG.ENDPOINTS.AI_BACKGROUND.MONITORING_STATUS);
    return response.data;
  }
  
  async getRedFlagAlerts() {
    const response = await this.client.get(API_CONFIG.ENDPOINTS.AI_BACKGROUND.RED_FLAG_ALERTS);
    return response.data;
  }
  
  async getChangeAlerts() {
    const response = await this.client.get(API_CONFIG.ENDPOINTS.AI_BACKGROUND.CHANGE_ALERTS);
    return response.data;
  }
  
  async getAnalytics() {
    const response = await this.client.get(API_CONFIG.ENDPOINTS.AI_BACKGROUND.ANALYTICS);
    return response.data;
  }
  
  // ATF Compliance methods
  async validateATFCompliance(organizationId) {
    const response = await this.client.get(API_CONFIG.ENDPOINTS.ATF_COMPLIANCE.VALIDATE, {
      params: { organization_id: organizationId }
    });
    return response.data;
  }
  
  // Documentation methods
  async getApiDocs() {
    const response = await this.client.get(API_CONFIG.ENDPOINTS.DOCUMENTATION.API_DOCS);
    return response.data;
  }
  
  async getTestSuite() {
    const response = await this.client.get(API_CONFIG.ENDPOINTS.DOCUMENTATION.TEST_SUITE);
    return response.data;
  }
  
  async getQuickStartGuide() {
    const response = await this.client.get(API_CONFIG.ENDPOINTS.DOCUMENTATION.QUICK_START);
    return response.data;
  }
  
  // Health check
  async healthCheck() {
    const response = await this.client.get('/');
    return response.data;
  }
  
  // Auth management
  setAuth(apiKey, jwtToken) {
    if (apiKey) {
      this.apiKey = apiKey;
      localStorage.setItem('dark_matter_api_key', apiKey);
    }
    
    if (jwtToken) {
      this.jwtToken = jwtToken;
      localStorage.setItem('dark_matter_jwt_token', jwtToken);
    }
    
    // Update client headers
    this.client.defaults.headers = API_CONFIG.getAuthHeaders(this.apiKey, this.jwtToken);
  }
  
  clearAuth() {
    this.apiKey = null;
    this.jwtToken = null;
    localStorage.removeItem('dark_matter_api_key');
    localStorage.removeItem('dark_matter_jwt_token');
    this.client.defaults.headers = API_CONFIG.DEFAULT_HEADERS;
  }
  
  isAuthenticated() {
    return !!(this.apiKey || this.jwtToken);
  }
  
  // Utility methods
  getBaseURL() {
    return this.baseURL;
  }
  
  getApiKey() {
    return this.apiKey;
  }
  
  getJwtToken() {
    return this.jwtToken;
  }
}

// Create singleton instance
const apiService = new ApiService();

export default apiService;
